import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { SoftCheckKind, softCheck } from '@shared/utils/soft-validators';

/**
 * Non-blocking advisory hint for a single form field. Give it the current
 * `value` of a control and the `kind` of check to run (email / phone / iban /
 * vat) and it shows a gentle amber warning when the value looks suspicious —
 * a wrong email domain, an implausible phone number, an IBAN/VAT that fails its
 * checksum. It NEVER affects form validity; it only helps the user catch a typo.
 *
 * Reactivity: it derives the warning from the `value` input via a `computed`,
 * so it updates under OnPush whenever the host re-binds `[value]` (which mat
 * inputs trigger on each keystroke/blur). Bind `[touched]` to stay quiet until
 * the user leaves the field, avoiding noise while they are still typing.
 *
 * Usage:
 * ```html
 * <mat-form-field class="w-full"> … </mat-form-field>
 * <app-field-advisory
 *   kind="email"
 *   [value]="form.get('email')?.value"
 *   [touched]="form.get('email')?.touched">
 * </app-field-advisory>
 * ```
 */
@Component({
  selector: 'app-field-advisory',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, TranslatePipe],
  template: `
    @if (message()) {
      <p class="field-advisory" role="status" aria-live="polite">
        <mat-icon class="field-advisory__icon">info</mat-icon>
        <span>{{ message() }}</span>
      </p>
    } @else if (advice(); as a) {
      <p class="field-advisory" role="status" aria-live="polite">
        <mat-icon class="field-advisory__icon">info</mat-icon>
        <span>{{ a.messageKey | translate: a.params }}</span>
      </p>
    }
  `,
  styles: [`
    .field-advisory {
      display: flex;
      align-items: flex-start;
      gap: 6px;
      margin: 2px 0 0;
      font-size: 12px;
      line-height: 1.35;
      color: #b45309; /* amber-700 */
    }
    .field-advisory__icon {
      width: 16px;
      height: 16px;
      font-size: 16px;
      flex: 0 0 auto;
      margin-top: 1px;
      color: #d97706; /* amber-600 */
    }
  `],
})
export class FieldAdvisoryComponent {
  /** Current value of the watched field. */
  readonly value = input<string | null | undefined>('');
  /** Which soft check to run. */
  readonly kind = input.required<SoftCheckKind>();
  /**
   * Gate the warning on the field having been touched/blurred. Defaults to
   * `true` so the component works even when a caller does not track touch state.
   */
  readonly touched = input<boolean | null | undefined>(true);
  /**
   * Un avis DÉJÀ RÉDIGÉ, fourni par le parent, qui prime sur le contrôle local.
   *
   * Certains avis ne se déduisent pas de la valeur du champ : « ce numéro est
   * déjà utilisé par Camille Verhoeven dans cet espace » demande une lecture
   * serveur, que ce composant n'a ni les moyens ni la vocation de faire.
   *
   * Il prime parce qu'il est PLUS SPÉCIFIQUE : « ce numéro semble implausible »
   * et « ce numéro appartient déjà à quelqu'un » ne se contredisent pas, mais
   * le second est actionnable et le premier générique. En afficher deux
   * empilerait deux lignes ambre sous un seul champ.
   *
   * Déjà traduit par le parent : la chaîne peut contenir un nom propre, que
   * `translate` ne saurait pas interpoler sans une clé dédiée par cas.
   */
  readonly message = input<string | null | undefined>(null);

  readonly advice = computed(() => (this.touched() ? softCheck(this.kind(), this.value()) : null));
}
